import { Dialog } from 'primereact/dialog';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { DataView } from 'primereact/dataview';
import { Button } from 'primereact/button';
import { ArrowLeft, ArrowRight, Check, TriangleAlert, X } from 'lucide-react';
import { MeterOptionIndividualReport } from '../../models/MeterOption';
import TargetIcon from '../../assets/target.svg';
import SupportIcon from '../../assets/support.svg';
import { SelectedSkillIndividualReport } from './IndividualReportWrapper';

export interface IndivudualModalState {
  key: string;
  skills: { id: string; name: string; code: string; score?: number }[];
}

export const modalInternalInfo: { [key: string]: { icon: JSX.Element | null; classNames: string; title: string } } = {
  demonstrated: {
    title: 'Demonstrated',
    icon: <Check size={15} className="tw-text-green-600" />,
    classNames: 'tw-bg-[#ECFEF6] tw-border-[#03BD7D] tw-border tw-border-solid tw-rounded-lg',
  },
  almost: {
    title: 'Almost there',
    icon: <TriangleAlert size={15} className="tw-text-yellow-600" />,
    classNames: 'tw-bg-[#FFF5EB] tw-border-[#AE7E09] tw-border tw-border-solid tw-rounded-lg',
  },
  review: {
    title: 'Review',
    icon: <TriangleAlert size={15} className="tw-text-yellow-600" />,
    classNames: 'tw-bg-[#FFF5EB] tw-border-[#AE7E09] tw-border tw-border-solid tw-rounded-lg',
  },
  keep: {
    title: 'Keep Learning',
    icon: <X size={15} className="tw-text-red-600" />,
    classNames: 'tw-bg-[#FCEDED] tw-border-[#B82828] tw-border tw-border-solid tw-rounded-lg',
  },
  not: {
    title: 'Not Started',
    icon: null,
    classNames: '',
  },
};
export const reviewScoreState = (score?: number) => {
  if (score !== undefined) {
    if (score >= 8) {
      return 'demonstrated';
    } else if (score === 6) {
      return 'almost';
    } else if (score < 6) {
      return 'keep';
    }
  }
  return 'not';
};
interface Props {
  meterValues: MeterOptionIndividualReport[];
  individualModalState: string | null;
  setIndividualModalState: Dispatch<SetStateAction<string | null>>;
  setSelectedSkill: Dispatch<SetStateAction<SelectedSkillIndividualReport | null>>;
}

export const IndividualStudentReportSkillsModal: FC<Props> = ({
  individualModalState,
  setIndividualModalState,
  meterValues,
  setSelectedSkill,
}) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const index = meterValues.findIndex(item => item.label === individualModalState);
    setIndex(index);
  }, [individualModalState]);

  const moveIndex = (x: -1 | 1) => {
    setIndex(prev => prev + x);
  };

  return (
    <Dialog
      onHide={() => {}}
      showHeader={false}
      breakpoints={{ '1280px': '60vw' }}
      className="nc_individual-report-skill-modal tw-w-[40%]"
      style={{ overflow: 'visible', maxHeight: '2000px' }}
      visible={!!individualModalState}
    >
      <div className="tw-flex tw-justify-between">
        <Button outlined style={{ border: 'none' }} disabled={index === 0} onClick={() => moveIndex(-1)}>
          <ArrowLeft size={20} />
        </Button>
        <h3 className="tw-text-center tw-mb-2">{modalInternalInfo[meterValues[index]?.label]?.title}</h3>
        <Button
          outlined
          style={{ border: 'none' }}
          disabled={index === meterValues.length - 1}
          onClick={() => moveIndex(1)}
        >
          <ArrowRight size={20} />
        </Button>
      </div>
      {meterValues[index]?.label === 'review' && (
        <div>
          <p className="tw-text-base tw-mb-0 tw-pb-0">
            Skills the student previously demonstrated but now needs to revisit, either because the student's latest
            diagnostic didn't confirm them or because the student didn't pass a later skill assessment.
          </p>
        </div>
      )}
      <div style={{ height: '72vh', marginTop: '1rem', marginBottom: 0, paddingBottom: 0 }}>
        <DataView
          footer={
            meterValues[index]?.label === 'demonstrated' && (
              <p>
                <b>Passed:</b> Skill was demonstrated by passing a skill assessment on related skill.
              </p>
            )
          }
          rows={10}
          paginatorLeft
          paginatorClassName="tw-absolute tw-bottom-12 tw-right-10 tw-mb-5 tw-pr-0"
          paginatorTemplate={{
            layout: 'CurrentPageReport PrevPageLink NextPageLink',
            CurrentPageReport: options => {
              return (
                <span
                  className="tw-text-neutral-500"
                  style={{ userSelect: 'none', width: '220px', textAlign: 'center' }}
                >
                  {options.first} - {options.last} of {options.totalRecords} skills shown
                </span>
              );
            },
          }}
          paginator
          value={meterValues[index]?.skills}
          itemTemplate={value => (
            <div
              key={value.id}
              className="tw-w-[100%] tw-h-[5vh] tw-flex tw-justify-between tw-items-center tw-py-1 tw-px-3 tw-mr-2 tw-border-[2px] tw-border-solid tw-border-[#F1F0EE] tw-rounded-lg tw-bg-neutral-0 tw-shadow-sm"
            >
              <p
                onClick={() => {
                  if (
                    meterValues[index].label === 'not' ||
                    (meterValues[index].label === 'demonstrated' && value.score === undefined)
                  ) {
                    return;
                  }
                  setSelectedSkill({ skillId: value.skillId, lastSession: null });
                }}
                className={`tw-text-blue-600 tw-font-semibold tw-flex tw-items-center ${
                  meterValues[index].label === 'not' ||
                  (meterValues[index].label === 'demonstrated' && value.score === undefined)
                    ? ''
                    : 'tw-cursor-pointer'
                }`}
              >
                <img className="tw-h-[1.5rem] tw-w-[1.5rem] tw-mr-2" src={value.isTarget ? TargetIcon : SupportIcon} />
                {value.name} ({value.code})
              </p>
              {meterValues[index]?.label !== 'not' && (
                <span
                  className={`${
                    meterValues[index]?.label === 'review'
                      ? modalInternalInfo[reviewScoreState(value.score)].classNames
                      : modalInternalInfo[meterValues[index]?.label]?.classNames
                  } tw-w-[5rem] tw-h-[90%] tw-font-semibold tw-py-1 tw-px-2 tw-rounded-md tw-flex tw-items-center ${
                    meterValues[index]?.label === 'review' ? '' : 'tw-border-[1px] tw-border-solid'
                  }`}
                >
                  {meterValues[index]?.label === 'review'
                    ? modalInternalInfo[reviewScoreState(value.score)].icon
                    : modalInternalInfo[meterValues[index]?.label]?.icon}
                  {value.score === undefined && meterValues[index]?.label === 'demonstrated' ? (
                    <span className="tw-ml-1">Passed</span>
                  ) : value.score === undefined && meterValues[index]?.label === 'review' ? (
                    <span className="tw-text-right tw-w-full">-</span>
                  ) : (
                    <span className="tw-ml-3">{value.score === 0 ? value.score : `${value.score}0`}%</span>
                  )}
                </span>
              )}
            </div>
          )}
        />
      </div>

      <div className="tw-flex tw-justify-end tw-mt-6 tw-pt-3 tw-pb-2 tw-h-[4rem]">
        <Button className="tw-mr-6 tw-w-[25%]" label="Close" outlined onClick={() => setIndividualModalState(null)} />
      </div>
    </Dialog>
  );
};
